import {groq} from 'next-sanity';

import type {KeyedArray} from '@/modules/foundation/shared/types';

import type {ProductInCard} from '../../shared/types';
import {DOCUMENT_TYPE_PRODUCT} from '../constants';
import type {ProductInCardFragment} from '../query';
import {PRODUCT_IN_CARD_FRAGMENT, productInCardFragmentToProductInCard} from '../query';
import {DOCUMENT_TYPE_SPECIAL_PRODUCTS} from '../special-product/constants';
import type {SpecialProductSchema} from '../special-product/schema';

import type {ProductListAllSchema} from './schema';

export const PRODUCT_LIST_ALL_FRAGMENT = groq`
	...,
	"products": *[_type == "${DOCUMENT_TYPE_PRODUCT}"] {
		${PRODUCT_IN_CARD_FRAGMENT}
	},
	"specialProducts": *[_type == "${DOCUMENT_TYPE_SPECIAL_PRODUCTS}"][0] {
		...,
		"category": category->{_id, title, orderRank},
		products[] {
			"slug": pageReference->slug.current,
			title,
			image,
			_key
		}
	}
`;

type ProductSchema = NonNullable<SpecialProductSchema['products']>[number];

type ProductImage = ProductSchema['image'];

type ProductFragment = {
	slug?: string;
	title: string;
	image?: ProductImage;
	_key: string;
};

type SpecialProductFragment = Omit<SpecialProductSchema, 'products' | 'category'> & {
	products: ProductFragment[];
	category?: {_id: string; title?: string; orderRank?: string} | null;
};

export type ProductListAllFragment = ProductListAllSchema & {
	products: ProductInCardFragment[];
	specialProducts: SpecialProductFragment;
};

type ProductType = {
	_id: string;
	name: string;
	orderRank?: string;
	products: KeyedArray<ProductInCard>;
};

type Category = {
	_id: string;
	name: string;
	orderRank?: string;
	productTypes: ProductType[];
	special?: true;
};

type ProductList = Category[];

// Sort by `orderRank` (the lexicographically-sortable string from
// @sanity/orderable-document-list), falling back to a Norwegian title compare
// when a rank is missing so unranked items still sort sanely and ranked items
// always precede unranked ones.
const byRankThenLabel = (
	rankA: string | undefined,
	labelA: string,
	rankB: string | undefined,
	labelB: string,
): number => {
	if (rankA && rankB) {
		if (rankA !== rankB) {
			return rankA < rankB ? -1 : 1;
		}
	} else if (rankA || rankB) {
		return rankA ? -1 : 1;
	}

	return labelA.localeCompare(labelB, 'nb');
};

export const productListFromProductListAllSection = (
	section: ProductListAllFragment,
): ProductList => {
	const categories: Category[] = [];

	// Reduce the products into grouped categories and product types
	const groupedCategories = (section.products ?? []).reduce((acc: Category[], sanityProduct) => {
		const product = productInCardFragmentToProductInCard(sanityProduct);

		if (!product) {
			return acc;
		}

		// Find or create the category
		let category = acc.find((cat) => cat._id === product.category._id);

		if (!category) {
			category = {
				_id: product.category?._id ?? '',
				name: product.category?.title ?? '',
				orderRank: product.category?.orderRank,
				productTypes: [],
			};
			acc.push(category);
		}

		// Find or create the product type within the category
		let productType = category.productTypes.find(
			(type) => type._id === product.productType._id,
		);

		if (!productType) {
			productType = {
				_id: product.productType?._id ?? '',
				name: product.productType?.title ?? '',
				orderRank: product.productType?.orderRank,
				products: [],
			};
			category.productTypes.push(productType);
		}

		// Add product to the product type (preventing duplicate products)
		const productExists = productType.products.some((p) => p._key === product._id);

		if (!productExists) {
			productType.products.push({...product, _key: product._id});
		}

		return acc;
	}, categories);

	// Spesialprodukter's contents live on a singleton, outside the Taxonomy —
	// but its tab is a real Category (referenced by the singleton), so it sorts
	// among the other tabs like any Category. Without the reference no entry is
	// added and the frontend falls back to pinning the tab last.
	const specialCategory = section.specialProducts?.category;

	if (specialCategory?._id) {
		groupedCategories.push({
			_id: specialCategory._id,
			name: specialCategory.title ?? 'Spesialprodukter',
			orderRank: specialCategory.orderRank,
			productTypes: [],
			special: true,
		});
	}

	// Sort each level by orderRank (title fallback): Categories (tabs), Types
	// within a Category, and Products within a Type.
	groupedCategories.sort((a, b) => byRankThenLabel(a.orderRank, a.name, b.orderRank, b.name));

	for (const category of groupedCategories) {
		category.productTypes.sort((a, b) =>
			byRankThenLabel(a.orderRank, a.name, b.orderRank, b.name),
		);

		for (const productType of category.productTypes) {
			productType.products.sort((a, b) =>
				byRankThenLabel(a.orderRank, a.title, b.orderRank, b.title),
			);
		}
	}

	return groupedCategories;
};
